import React, { useEffect, useState } from "react";
import { Card, Tag, Row, Col, Spin } from "antd";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
} from "recharts";
import { getData } from "../../../../../../actions/apiCall";
import { useDispatch, useSelector } from "react-redux";
import dayjs, { Dayjs } from "dayjs";

/* ================= CONFIG ================= */

interface AverageDeploymentSummaryProps {
  bdeId?: string | null;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  period?: string;
}

const defaultKpiCards = [
  {
    title: "Average Leads Created",
    value: 23,
    change: "+4.6%",
    trendUp: true,
    color: "#2563eb",
    data: [
      { v: 10 },
      { v: 12 },
      { v: 9 },
      { v: 14 },
      { v: 11 },
      { v: 16 },
      { v: 18 },
      { v: 15 },
    ],
  },
  {
    title: "Average Deals Created",
    value: 23,
    change: "+4.6%",
    trendUp: true,
    color: "#f97316",
    data: [
      { v: 8 },
      { v: 10 },
      { v: 7 },
      { v: 11 },
      { v: 9 },
      { v: 13 },
      { v: 15 },
      { v: 14 },
    ],
  },
  {
    title: "Average Accounts Created",
    value: 23,
    change: "-1.6%",
    trendUp: false,
    color: "#22c55e",
    data: [
      { v: 12 },
      { v: 15 },
      { v: 11 },
      { v: 14 },
      { v: 13 },
      { v: 16 },
      { v: 18 },
      { v: 16 },
    ],
  },
];

const deploymentConfig = {
  target: 4,
  status: "Behind Target",
  chart: [
    { day: "Sun", value: 0.2 },
    { day: "Mon", value: 1 },
    { day: "Tue", value: 0.2 },
    { day: "Wed", value: 0.2 },
    { day: "Thu", value: 1 },
    { day: "Fri", value: 0.2 },
    { day: "Sat", value: 0.3 },
  ],
  summary: {
    deployed: "2 Developers",
    achieved: "66% Achieved",
    best: "Week 2 - 2 Deployments",
  },
};

/* ================= COMPONENT ================= */

const AverageDeploymentSummary: React.FC<AverageDeploymentSummaryProps> = ({ 
  bdeId, 
  startDate, 
  endDate, 
  period = 'month' 
}) => {
  const [trackerData, setTrackerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { user, isGlobalAdmin } = useSelector((state: any) => state.app);
  const isAdmin = isGlobalAdmin || user?.designation === 'Admin';

  useEffect(() => {
    if (startDate && endDate && (isAdmin || bdeId)) {
      fetchTrackerData();
    }
  }, [bdeId, startDate, endDate, period, isAdmin]);

  const fetchTrackerData = async () => {
    if (!startDate || !endDate) return;
    if (!isAdmin && !bdeId) return;

    try {
      setLoading(true);
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const bdeIdParam = bdeId === 'all' || !bdeId ? 'all' : bdeId;

      const res = await dispatch(
        getData(`/targets/bde/tracker-dashboard?bdeId=${bdeIdParam}&startDate=${startDateStr}&endDate=${endDateStr}&period=${period}`)
      );

      if (res?.code === 200 && res?.data) {
        setTrackerData(res.data);
      }
    } catch (error) {
      console.error('Error fetching average deployment data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use API data if available, otherwise use defaults
  const kpiCards = trackerData?.averages ? trackerData.averages.map((avg: any) => ({
    ...avg,
    data: Array.from({ length: 8 }, () => ({ v: Math.random() * 20 + 5 })) // Generate trend data
  })) : defaultKpiCards;

  // Get deployment data (this would need to be added to API if not already there)
  const deploymentConfig = {
    target: 4,
    status: "Behind Target",
    chart: [
      { day: "Sun", value: 0.2 },
      { day: "Mon", value: 1 },
      { day: "Tue", value: 0.2 },
      { day: "Wed", value: 0.2 },
      { day: "Thu", value: 1 },
      { day: "Fri", value: 0.2 },
      { day: "Sat", value: 0.3 },
    ],
    summary: {
      deployed: "2 Developers",
      achieved: "66% Achieved",
      best: "Week 2 - 2 Deployments",
    },
  };

  return (
    <div className="mt-6">
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* LEFT KPI CARDS */}
          <div className="col-span-12 lg:col-span-4 flex flex-col space-y-6 h-full">
            {kpiCards.map((item: any) => (
            <Card
              key={item.title}
              className="rounded-xl shadow-sm flex-1 flex flex-col justify-between"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500">{item.title}</p>
                  <h2 className="text-2xl font-semibold mt-1">{item.value}</h2>
                  <p
                    className={`text-sm mt-1 flex items-center gap-1 ${
                      item.trendUp ? "text-green-600" : "text-red-500"
                    }`}
                  >
                    {item.change}
                    <span>{item.trendUp ? "▲" : "▼"}</span>
                  </p>
                </div>

                <div className="w-28 h-12">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={item.data}>
                      <Area
                        type="monotone"
                        dataKey="v"
                        stroke={item.color}
                        fillOpacity={0.15}
                        fill={item.color}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* RIGHT DEPLOYMENT CARD */}
        <div className="col-span-12 lg:col-span-8 h-full">
          <Card className="rounded-xl shadow-sm flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold">Developer Deployment</h3>
              <Tag color="orange">{deploymentConfig.status}</Tag>
            </div>

            <p className="text-sm text-gray-500 mb-2">
              Target:{" "}
              <span className="font-medium text-gray-700">
                {deploymentConfig.target} Deployment/month
              </span>
            </p>

            <Card type="inner" className="mb-4 flex-1 left_average">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={deploymentConfig.chart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" />
                    <YAxis domain={[0, 4]} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#14b8a6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Row gutter={0} className="text-sm">
              <Col span={8} className="border-r border-gray-200 p-2">
                <p className="text-gray-500">Developers Deployed</p>
                <p className="font-medium">{deploymentConfig.summary.deployed}</p>
              </Col>
              <Col span={8} className="border-r border-gray-200 p-2">
                <p className="text-gray-500">Target</p>
                <p className="font-medium">{deploymentConfig.summary.achieved}</p>
              </Col>
              <Col span={8} className="p-2">
                <p className="text-gray-500">Best</p>
                <p className="font-medium">{deploymentConfig.summary.best}</p>
              </Col>
            </Row>
          </Card>
        </div>
        </div>
      )}
    </div>
  );
};

export default AverageDeploymentSummary;
